/**
 * Azure AD SSO, implemented directly against the ABG SSO service.
 *
 * The flow the SSO service expects:
 *
 *  1. Browser is sent to `${ssoBaseUrl}/oidc/login?appVar=<appVar>` — a full page
 *     navigation, never fetch/XHR. The SSO service issues its own JSESSIONID on its
 *     own origin and hands the browser off to Azure.
 *  2. Azure returns to the SSO service's callback (`/sso/login/oauth2/code/azure`),
 *     which is why the browser must talk to the SSO host directly: the session cookie
 *     and the Azure callback have to land on the same origin.
 *  3. The SSO service redirects back to the frontend URL registered for this `appVar`
 *     in its database, appending one of:
 *       - `?token=<ssoToken>&sso=1&provider=microsoft` on success
 *       - `?ssoError=<message>`                        on failure
 *       - `?oauth2fa=true`                             when a second factor is needed
 *  4. The frontend exchanges that short-lived SSO token for the app's own JWT pair by
 *     POSTing to the auth service with `Authorization: Bearer <ssoToken>` and the
 *     `appVar` header. The response is the same shape as a password login, so the
 *     session is stored through the very same helpers.
 *
 * `appVar` is what the SSO service uses to pick the portal, its default role and the
 * frontend URL to return to; it must match a portal registered in the SSO database.
 * This app is registered as `avclient`.
 */

import type { AuthSessionContent } from './helpers';

/** Query parameters the SSO service appends when it returns the browser to this app. */
export interface SsoCallback {
    /** Short-lived SSO token to exchange for the app's own JWT pair. */
    token: string | null;
    /** Human-readable failure message produced by the SSO service. */
    error: string | null;
    /** The account requires a second factor, which this app does not implement. */
    twoFactor: boolean;
}

const SSO_CALLBACK_PARAMS = ['token', 'sso', 'provider', 'ssoError', 'oauth2fa'];

// Read through process.env at call time rather than destructuring at module scope, so
// the values stay stubbable from tests. CRA still inlines each static reference.
const ssoBaseUrl = (): string =>
    String(process.env.REACT_APP_SSO_BASE_URL ?? '').trim().replace(/\/+$/, '');

const appVar = (): string => String(process.env.REACT_APP_APP_VAR ?? '').trim();

/**
 * The endpoint the SSO token is exchanged at — resolved exactly the way LoginAPI
 * resolves it for password login, because both credentials must mint a session at the
 * same place. REACT_APP_BASE_URL points at the avatar-core service, so the
 * "/avatar-core/" segment is dropped to get back to the gateway root.
 */
const loginUrl = (): string => {
    const base = String(process.env.REACT_APP_BASE_URL ?? '')
        .replace('/avatar-core/', '')
        .replace(/\/+$/, '');
    return `${base}/sc/api/auth`;
};

/**
 * Whether SSO can be offered at all. Both the SSO host and the portal identifier are
 * required — the SSO service answers 500 when `appVar` is missing.
 */
export const isSsoConfigured = (): boolean => !!ssoBaseUrl() && !!appVar();

/**
 * Hands the browser to the SSO service. This never returns: it is a top-level
 * navigation, not a request, so the SSO service's session cookie is set on its own
 * origin and the Azure callback finds it.
 */
export const startSsoLogin = (): void => {
    window.location.assign(`${ssoBaseUrl()}/oidc/login?appVar=${encodeURIComponent(appVar())}`);
};

/** Reads the SSO result off the current URL. All fields are absent on a normal visit. */
export const readSsoCallback = (): SsoCallback => {
    const params = new URL(window.location.href).searchParams;
    return {
        token: params.get('token'),
        error: params.get('ssoError'),
        twoFactor: params.get('oauth2fa') === 'true',
    };
};

/** True when this page load is an SSO return, whatever its outcome. */
export const hasSsoCallback = (callback: SsoCallback): boolean =>
    !!callback.token || !!callback.error || callback.twoFactor;

/**
 * Strips the SSO parameters from the address bar without reloading, so the token is
 * not left in history, bookmarks or the Referer header, and a refresh does not try to
 * replay an already-consumed token.
 */
export const clearSsoCallbackParams = (): void => {
    const url = new URL(window.location.href);
    SSO_CALLBACK_PARAMS.forEach((param) => url.searchParams.delete(param));
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

/**
 * Exchanges the SSO token for this app's own access/refresh tokens.
 *
 * Same endpoint as the password login — only the credential differs: a Bearer SSO
 * token instead of Basic credentials. Throws when the exchange fails or when the
 * account still needs a second factor (no accessToken in the response).
 */
export const exchangeSsoToken = async (ssoToken: string): Promise<AuthSessionContent> => {
    const response = await fetch(loginUrl(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${ssoToken}`,
            appVar: appVar(),
        },
        body: JSON.stringify({}),
    });

    if (!response.ok) {
        throw new Error(`SSO token exchange failed with status ${response.status}`);
    }

    const json = await response.json().catch(() => null);
    const content = json?.content ?? json;
    if (!content?.accessToken) {
        throw new Error('SSO token exchange returned no access token');
    }

    return {
        accessToken: content.accessToken,
        refreshToken: content.refreshToken,
        firstName: content.firstName,
        lastName: content.lastName,
        identity: content.identity,
    };
};
